import { Button, ButtonGroup } from '@eleveo/components-react';
import React, { useEffect, useState } from 'react';
import { FormProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { ScheduleElementForm } from './ScheduleElementForm';
import { DEFAULTS_SCHEDULE_ELEMENTS, initialScheduleElement } from './config';
import { BUTTON_CANCEL_SCHEDULE_ELEMENT, BUTTON_SUBMIT_SCHEDULE_ELEMENT } from './constants';
import { normalizeScheduleElement } from './utils';
import { SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import { SidebarForm } from '../../components/LayoutNext/SidebarForm';
import { type ScheduleElement, useGetQueuesTagsQuery } from '../../graphql/components';

import type { ScheduleElementValues } from './types';

export type ScheduleElementsSidebarProps = {
  scheduleElement?: ScheduleElement;
  handleCancel: () => void;
  handleSubmit: FormProps<ScheduleElementValues>['onSubmit'];
};

export const ScheduleElementsSidebar: React.FC<ScheduleElementsSidebarProps> = ({
  handleCancel,
  handleSubmit,
  scheduleElement,
}) => {
  const { t } = useTranslation();

  const [initialValues, setInitialValues] = useState<ScheduleElementValues>(initialScheduleElement);
  const tagsResult = useGetQueuesTagsQuery();

  useEffect(() => {
    if (scheduleElement) {
      setInitialValues(normalizeScheduleElement(scheduleElement));
    } else {
      setInitialValues(initialScheduleElement);
    }
  }, [scheduleElement]);

  return (
    <SidebarForm<ScheduleElementValues>
      initialValues={initialValues}
      name="schedule-element-form"
      onClose={handleCancel}
      onSubmit={handleSubmit}
      title={t(`scheduleElements.${scheduleElement ? 'edit' : 'add'}Element`)}
    >
      {({ error, submitErrors, dirtySinceLastSubmit, pristine }) => (
        <>
          <SidebarPanel>
            <ButtonGroup>
              <Button
                isDisabled={pristine || error || (Object.keys(submitErrors ?? {}).length && !dirtySinceLastSubmit)}
                color="success"
                type="submit"
                testId={BUTTON_SUBMIT_SCHEDULE_ELEMENT}
              >
                {t('layout.action.save')}
              </Button>

              <Button onClick={handleCancel} testId={BUTTON_CANCEL_SCHEDULE_ELEMENT} variant="outline">
                {t('layout.action.cancel')}
              </Button>
            </ButtonGroup>
          </SidebarPanel>

          <SidebarContent>
            <SidebarPart>
              <ScheduleElementForm
                tagsResult={tagsResult}
                isEditable={scheduleElement ? !!scheduleElement.editable : true}
                isDefaultElement={scheduleElement && DEFAULTS_SCHEDULE_ELEMENTS.includes(scheduleElement.name)}
              />
            </SidebarPart>
          </SidebarContent>
        </>
      )}
    </SidebarForm>
  );
};
